import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatValue } from "@/lib/api";
import PlayerBadge from "@/components/PlayerBadge";
import { ShareButtons } from "@/components/ShareButtons";
import { Loader2, AlertTriangle, ArrowLeftRight, TrendingUp, TrendingDown, Newspaper, Telescope, Sparkles } from "lucide-react";

const TIER_COLORS = { 1: "#16A34A", 2: "#0284C7", 3: "#7C3AED", 4: "#6B7280", 5: "#9CA3AF" };

function ChangeChip({ pct }) {
  if (!pct) return <span className="text-[var(--text-3)] font-mono text-xs">—</span>;
  const up = pct > 0;
  return (
    <span className={`font-mono text-xs inline-flex items-center gap-0.5 ${up ? "text-[var(--accent)]" : "text-[var(--negative)]"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? "+" : ""}{pct}%
    </span>
  );
}

function ScoutReport({ player }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setOpen(o => !o);
    if (report || loading) return;
    setLoading(true);
    try {
      const r = await api.get(`/rookies/${player.player_id}/scout-report`);
      setReport(r.data.report);
    } catch { setReport("Couldn't generate a report right now."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={load} data-testid={`scout-report-btn-${player.player_id}`}
        className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm border border-[var(--accent)]/40 text-[var(--accent)] hover:bg-[var(--accent)]/10 transition-colors">
        <Sparkles className="w-3 h-3" /> {open ? "HIDE" : "AI REPORT"}
      </button>
      {open && (
        <div className="col-span-12 mt-2 mb-1 rounded-sm border border-[var(--border)] bg-black/40 p-3" data-testid={`scout-report-${player.player_id}`}>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[var(--text-3)]"><Loader2 className="w-3.5 h-3.5 spin" /> Scouting {player.name}…</div>
          ) : (
            <div className="prose-dme text-sm text-[var(--text-2)] max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{report || ""}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function DevyBoard({ d }) {
  const board = d.big_board || [];
  return (
    <div data-testid="ctx-rookies" className="space-y-5">
      <div className="text-sm text-[var(--text-2)] flex items-center gap-2">
        <Telescope className="w-4 h-4 text-[var(--accent)]" />
        {d.note || `Showing the ${d.season} Devy class.`}
      </div>
      {board.length === 0 ? (
        <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]" data-testid="devy-empty">
          The {d.season} Devy class hasn't been seeded yet — it self-populates as prospects declare.
        </div>
      ) : (
        <div className="card-bordered overflow-hidden" data-testid="devy-big-board">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] tracking-[0.15em] text-[var(--text-3)] font-mono border-b border-[var(--border)]">
            <div className="col-span-1">#</div>
            <div className="col-span-6">PROSPECT</div>
            <div className="col-span-3">SCHOOL</div>
            <div className="col-span-2 text-right">TIER</div>
          </div>
          {board.map(p => (
            <div key={`${p.rank}-${p.name}`} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0" data-testid={`devy-row-${p.rank}`}>
              <div className="col-span-1 font-mono text-[var(--text-3)]">{p.rank}</div>
              <div className="col-span-6 min-w-0">
                <span className="truncate block">{p.name}</span>
                <div className="text-[10px] tracking-widest text-[var(--text-3)]">
                  {p.position}{p.class_year ? ` · ${p.class_year}` : ""} · <span className="text-[var(--accent)]">Declares in {d.season}</span>
                </div>
                {p.note && <div className="text-[11px] text-[var(--text-3)] italic mt-0.5 truncate">{p.note}</div>}
              </div>
              <div className="col-span-3 text-[var(--text-2)] truncate">{p.school || "—"}</div>
              <div className="col-span-2 text-right">
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border" style={{ borderColor: (TIER_COLORS[p.tier] || "#525252") + "66", color: TIER_COLORS[p.tier] || "#7A8A7A" }}>
                  {p.tier_label || "—"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RookieScoutPanel({ id }) {
  const [d, setD] = useState(null);
  const load = () => {
    setD(null);
    api.get(`/leagues/${id}/rookie-scout`).then(r => setD(r.data)).catch(() => setD({ error: true }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (d === null) return <div className="card-bordered p-12 flex justify-center" data-testid="rookies-loading"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;
  if (d.error || !d.big_board) return (
    <div className="card-bordered p-8 text-center" data-testid="rookies-error">
      <p className="text-sm text-[var(--text-3)] mb-4">Live data is busy right now — the rookie board didn't load.</p>
      <button onClick={load} data-testid="rookies-retry" className="btn-secondary text-sm inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5" /> Retry</button>
    </div>
  );

  if (d.board_mode === "devy") return <DevyBoard d={d} />;

  return (
    <div data-testid="ctx-rookies" className="space-y-5">
      <div className="text-sm text-[var(--text-2)] flex items-center gap-2">
        <Telescope className="w-4 h-4 text-[var(--accent)]" />
        {d.season} rookie class scouting — DME-valued big board for your league format, with value movement, AI scouting reports, and a value-based trade-back calculator. Self-updates as the class declares & values move.
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Big board */}
        <div className="lg:col-span-2 card-bordered overflow-hidden" data-testid="rookie-big-board">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] tracking-[0.15em] text-[var(--text-3)] font-mono border-b border-[var(--border)]">
            <div className="col-span-1">#</div>
            <div className="col-span-5">ROOKIE</div>
            <div className="col-span-2 text-right text-[var(--accent)]">DME</div>
            <div className="col-span-2 text-right">7D</div>
            <div className="col-span-2 text-right">REPORT</div>
          </div>
          {d.big_board.map(p => (
            <div key={p.player_id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0" data-testid={`rookie-row-${p.player_id}`}>
              <div className="col-span-1 font-mono text-[var(--text-3)]">{p.rank}</div>
              <div className="col-span-5 min-w-0">
                <span className="flex items-center gap-1.5 min-w-0">
                  <Link to={`/app/players/${p.player_id}`} className="hover:text-[var(--accent)] truncate">{p.name}</Link>
                  <PlayerBadge badge={p.badge} />
                </span>
                <div className="text-[10px] tracking-widest text-[var(--text-3)]">
                  {p.position} · {p.team}{p.age ? ` · ${p.age}y` : ""} · <span style={{ color: TIER_COLORS[p.tier] }}>{p.tier_name}</span>
                </div>
              </div>
              <div className="col-span-2 text-right font-mono text-[var(--accent)]">{formatValue(p.value)}</div>
              <div className="col-span-2 text-right"><ChangeChip pct={p.change_7d} /></div>
              <div className="col-span-2 text-right"><ScoutReport player={p} /></div>
            </div>
          ))}
        </div>

        {/* Sidebar: movers, trade-back, alerts, news */}
        <div className="space-y-4">
          {(d.risers?.length > 0 || d.fallers?.length > 0) && (
            <div className="card-bordered p-4" data-testid="rookie-movers">
              <div className="text-[10px] tracking-widest font-mono text-[var(--text-3)] mb-2">DRAFT STOCK · 7D</div>
              {d.risers?.length === 0 && d.fallers?.length === 0 && <div className="text-xs text-[var(--text-3)]">No movement yet — values are tracking.</div>}
              {d.risers?.map(p => (
                <div key={p.player_id} className="flex items-center justify-between text-xs py-1">
                  <Link to={`/app/players/${p.player_id}`} className="truncate hover:text-[var(--accent)]">{p.name}</Link>
                  <ChangeChip pct={p.change_7d} />
                </div>
              ))}
              {d.fallers?.map(p => (
                <div key={p.player_id} className="flex items-center justify-between text-xs py-1">
                  <Link to={`/app/players/${p.player_id}`} className="truncate hover:text-[var(--negative)]">{p.name}</Link>
                  <ChangeChip pct={p.change_7d} />
                </div>
              ))}
            </div>
          )}

          {d.tier_alerts?.length > 0 && (
            <div className="card-bordered p-4" data-testid="rookie-tier-alerts">
              <div className="flex items-center gap-2 text-[10px] tracking-widest font-mono text-[var(--amber)] mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> TIER DEPLETION
              </div>
              {d.tier_alerts.map((a, i) => <p key={i} className="text-xs text-[var(--text-2)] mb-2 last:mb-0">{a.message}</p>)}
            </div>
          )}

          <div className="card-bordered p-4" data-testid="rookie-tradeback">
            <div className="flex items-center gap-2 text-[10px] tracking-widest font-mono text-[var(--accent)] mb-3">
              <ArrowLeftRight className="w-3.5 h-3.5" /> TRADE-BACK CALCULATOR
            </div>
            {(!d.trade_backs || d.trade_backs.length === 0) ? (
              <div className="text-xs text-[var(--text-3)]">No value-positive trade-backs from your first pick right now.</div>
            ) : d.trade_backs.map((t, i) => (
              <div key={i} className="border-b border-[var(--border)] last:border-0 pb-3 mb-3 last:pb-0 last:mb-0" data-testid={`rookie-tradeback-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold truncate">{t.partner}</span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-[var(--accent)]/40 text-[var(--accent)]">+{t.surplus_pct}%</span>
                </div>
                <div className="text-xs text-[var(--text-2)]">
                  <span className="text-[var(--negative)]">↑ {t.user_gives.pick_label}</span>
                  <span className="text-[var(--text-3)] mx-1">for</span>
                  <span className="text-[var(--accent)]">↓ {t.user_gets.map(g => g.pick_label).join(" + ")}</span>
                </div>
                <p className="text-[11px] text-[var(--text-3)] mt-1 italic">{t.note}</p>
              </div>
            ))}
          </div>

          {d.news?.length > 0 && (
            <div className="card-bordered p-4" data-testid="rookie-news">
              <div className="flex items-center gap-2 text-[10px] tracking-widest font-mono text-[var(--text-3)] mb-2">
                <Newspaper className="w-3.5 h-3.5" /> ROOKIE NEWS
              </div>
              {d.news.map((n, i) => (
                <a key={i} href={n.link} target="_blank" rel="noreferrer" className="block text-xs text-[var(--text-2)] hover:text-[var(--accent)] py-1.5 border-b border-[var(--border)] last:border-0" data-testid={`rookie-news-${i}`}>
                  {n.matched_player && <span className="text-[var(--accent)] font-mono text-[10px]">{n.matched_player} · </span>}{n.title}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
